import React, { useState, useEffect } from "react";
import { Entidade, useEntidade } from "../../../../Hooks/EntidadeHook";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Divider,
} from "@mui/material";

// Função para classificar entidades corretamente
const getCategoria = (
  titulo: string
): "Prefeitura" | "Câmara" | "Consórcio" | "Outros" => {
  const tituloLower = titulo.toLowerCase();
  if (tituloLower.includes("prefeitura")) return "Prefeitura";
  if (tituloLower.includes("câmara")) return "Câmara";
  if (tituloLower.includes("consórcio")) return "Consórcio";
  return "Outros";
};

const normalizarTexto = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Função para obter o nome real da entidade sem "Prefeitura - ", "Câmara - " etc.
const getNomeLimpo = (titulo: string) =>
  titulo.replace(/^(Prefeitura|Câmara|Consórcio) - /i, "").trim();

export const Entidades = () => {
  const { entidades, setEntidadeFocus } = useEntidade();
  const [filtro, setFiltro] = useState("Prefeitura");
  const [busca, setBusca] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Pega o parâmetro "entidade" da URL
  const entidadeBanco = searchParams.get("entidade");

  // Seleciona a entidade automaticamente se houver um parâmetro na URL
  useEffect(() => {
    if (entidadeBanco) {
      const entidadeSelecionada = entidades.find(
        (entidade) => entidade.banco === entidadeBanco
      );

      if (entidadeSelecionada) {
        setEntidadeFocus(entidadeSelecionada);
      }
    }
  }, [entidadeBanco, entidades]);



  // Mapeamento de categorias para ordenação
  const ordemCategorias: Record<string, number> = {
    Prefeitura: 0,
    Câmara: 1,
    Consórcio: 2,
    Outros: 3,
  };

  // Filtra entidades pelo tipo selecionado e nome digitado
  const entidadesFiltradas = entidades
    .filter((entidade) => {
      const categoria = getCategoria(entidade.titulo);
      const correspondeCategoria = filtro === "Todos" || categoria === filtro;

      const nomeNormalizado = normalizarTexto(
        getNomeLimpo(entidade.titulo)
      );

      const buscaNormalizada = normalizarTexto(busca);

      const correspondeBusca = nomeNormalizado.includes(buscaNormalizada);

      return correspondeCategoria && correspondeBusca;
    })
    .sort((a, b) => {
      const categoriaA = getCategoria(a.titulo);
      const categoriaB = getCategoria(b.titulo);
      const nomeA = normalizarTexto(getNomeLimpo(a.titulo));
      const nomeB = normalizarTexto(getNomeLimpo(b.titulo));

      if (ordemCategorias[categoriaA] !== ordemCategorias[categoriaB]) {
        return ordemCategorias[categoriaA] - ordemCategorias[categoriaB];
      }

      return nomeA.localeCompare(nomeB);
    });




  // Verifica se há entidades "Outros" para remover a categoria se estiver vazia
  const temOutros = entidadesFiltradas.some(
    (entidade) => getCategoria(entidade.titulo) === "Outros"
  );

  let ultimaCategoria = "";

  return (
    <Box sx={{ width: "100%", maxWidth: "1000px", margin: "auto", p: 3 }}>
      {/* Filtros */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
        }}
      >
        <FormControl sx={{ flex: 1, minWidth: 180 }}>
          <InputLabel>Filtrar por Categoria</InputLabel>
          <Select
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            sx={{ borderRadius: 2, bgcolor: "background.paper" }}
          >
            <MenuItem value="Todos">Todos</MenuItem>
            <MenuItem value="Prefeitura">Prefeituras</MenuItem>
            <MenuItem value="Câmara">Câmaras</MenuItem>
            <MenuItem value="Consórcio">Consórcios</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Buscar por nome"
          variant="outlined"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ borderRadius: 2, bgcolor: "background.paper", minWidth: 250 }}
        />
      </Box>

      {/* Grid Responsivo de Cartões */}
      <Grid container spacing={2}>
        {entidadesFiltradas.map((entidade, index) => {
          const categoriaAtual = getCategoria(entidade.titulo);

          // Evita exibir "Outros" se não há entidades dessa categoria
          if (categoriaAtual === "Outros" && !temOutros) return null;

          const mostrarDivider =
            categoriaAtual !== ultimaCategoria && index !== 0;
          ultimaCategoria = categoriaAtual;

          return (
            <React.Fragment key={entidade.id}>
              {mostrarDivider && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        textAlign: "center",
                        color: "#1565C0",
                        letterSpacing: 1,
                      }}
                    >
                      {categoriaAtual}
                    </Typography>
                  </Divider>
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Card
                  sx={{
                    height: 170,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: 3,
                    transition: "0.3s",
                    "&:hover": { transform: "scale(1.05)" },
                    p: 1,
                  }}
                >
                  <CardMedia
                    component="img"
                    height="80"
                    image={entidade.logo || "https://via.placeholder.com/100"}
                    alt={entidade.titulo}
                    sx={{
                      objectFit: "contain",
                      p: 1,
                      background: "#fff",
                      borderRadius: 1,
                      maxHeight: "80px",
                      maxWidth: "100px",
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1, textAlign: "center", p: 0 }}>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: 14, fontWeight: "bold" }}
                    >
                      {getNomeLimpo(entidade.titulo)}
                    </Typography>
                  </CardContent>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      width: "100%",
                      borderRadius: 2,
                      fontSize: 12,
                      py: 0.5,
                    }}
                    onClick={() => {
                      setEntidadeFocus(entidade);
                      navigate(`?entidade=${entidade.banco}`);
                    }}
                  >
                    Selecionar
                  </Button>
                </Card>
              </Grid>
            </React.Fragment>
          );
        })}
      </Grid>
    </Box>
  );
};
